import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Target } from 'lucide-react';
import { buildIncomeSeries, CHART_MODES } from '../../utils/incomeChartSeries';

/**
 * Target vs sales across the twelve months of a reporting year.
 *
 * Hand-rolled SVG rather than a charting dependency: this is one twelve-point plot with
 * two series, and the smallest chart library in the ecosystem is larger than everything
 * else on this page put together.
 *
 * All the arithmetic — which months a line may span, what a missing target does to it,
 * where the axis tops out — lives in utils/incomeChartSeries.js and is unit tested. This
 * file only turns numbers into coordinates.
 */

// Emerald-600 and orange-600. Chosen against a colour-vision check rather than by eye:
// the pair clears ΔE 10.1 under protanopia and both clear 3:1 on white, which the
// lighter Tailwind steps of the same hues do not. Do not "brighten" these without
// re-running that check — orange-500 fails contrast at 2.73:1.
const TARGET_COLOR = '#059669';
const SALES_COLOR = '#ea580c';
/* The two bands above the paid line. Deliberately paler than it: money you have is the
   thing to read first, and money you merely hope for must never out-shout it. */
const AWAITING_COLOR = '#fb923c';
const DRAFT_COLOR = '#fcd34d';

const GRID = '#f3f4f6';     // gray-100
const AXIS = '#e5e7eb';     // gray-200
const SURFACE = '#ffffff';

const PAD = { top: 18, right: 76, bottom: 34, left: 68 };
const HEIGHT = 330;
const MIN_WIDTH = 360;

/** Compact currency for axis ticks — "$2.5K" keeps twelve labels off each other. */
const AXIS_FORMAT = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    notation: 'compact',
    maximumFractionDigits: 1,
});

const FULL_FORMAT = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const WHOLE_FORMAT = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
});

const formatAxisTick = (value) => (value === 0 ? '$0' : AXIS_FORMAT.format(value));

/**
 * The value that rides the end of a line. Compact rounding turns $92.40 into the
 * half-finished "$92.4", so anything under a thousand keeps its cents and everything
 * above it drops them — "$28,650" rather than "$28.7K", which is the figure a workshop
 * would actually repeat out loud.
 */
const formatEndLabel = (value) => (Math.abs(Number(value) || 0) < 1000
    ? FULL_FORMAT.format(value)
    : WHOLE_FORMAT.format(value));

const formatFull = (value) => FULL_FORMAT.format(Number(value) || 0);
const formatSigned = (value) => {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    return `${n < 0 ? '-' : '+'}${FULL_FORMAT.format(Math.abs(n))}`;
};

/** A legend key mirrors its mark: a stroke for a line, dashed where the line is dashed. */
const LegendKey = ({ color, dashed }) => (
    <svg width="18" height="8" aria-hidden="true" className="shrink-0">
        <line
            x1="0" y1="4" x2="18" y2="4"
            stroke={color} strokeWidth="2" strokeLinecap="round"
            strokeDasharray={dashed ? '6 5' : undefined}
        />
    </svg>
);

const IncomeChart = ({
    months,
    mode = CHART_MODES.MONTHLY,
    currentYear,
    currentMonth,
    periodLabel,
    onSetTarget,
}) => {
    const wrapRef = useRef(null);
    const [measuredWidth, setMeasuredWidth] = useState(0);
    const [activeIndex, setActiveIndex] = useState(null);

    // Rendered at real pixel width rather than scaled from a fixed viewBox, so the axis
    // text stays 11px on a laptop and on a 32" monitor alike.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const measure = () => setMeasuredWidth(el.clientWidth);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const series = useMemo(
        () => buildIncomeSeries(months, { mode, currentYear, currentMonth }),
        [months, mode, currentYear, currentMonth],
    );

    const {
        points, salesSegments, awaitingSegments, possibleSegments, targetSegments,
        yMax, ticks, hasAnyTarget, hasAnyAwaiting, hasAnyDraft, cumulative, weekly, targetIsPartial,
    } = series;

    const width = Math.max(measuredWidth || MIN_WIDTH, MIN_WIDTH);
    const plotWidth = Math.max(width - PAD.left - PAD.right, 10);
    const plotHeight = HEIGHT - PAD.top - PAD.bottom;

    const xOf = (index) => (points.length > 1
        ? PAD.left + (index * plotWidth) / (points.length - 1)
        : PAD.left + plotWidth / 2);
    const yOf = (value) => PAD.top + plotHeight * (1 - (Number(value) || 0) / yMax);

    const pathOf = (run, field) => run
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.index).toFixed(2)} ${yOf(p[field]).toFixed(2)}`)
        .join(' ');

    /**
     * A filled band between two stacked edges: forward along the top, back along the
     * bottom. Built from the SAME point runs as the lines, so a band can never span a
     * month the line does not — which is what would let a future month be shaded in.
     */
    const bandOf = (run, upperField, lowerField) => {
        const forward = run.map((p, i) =>
            `${i === 0 ? 'M' : 'L'} ${xOf(p.index).toFixed(2)} ${yOf(p[upperField]).toFixed(2)}`);
        const back = [...run].reverse().map((p) =>
            `L ${xOf(p.index).toFixed(2)} ${yOf(p[lowerField]).toFixed(2)}`);
        return `${forward.join(' ')} ${back.join(' ')} Z`;
    };

    const lastOf = (segments) => {
        const run = segments[segments.length - 1];
        return run ? run[run.length - 1] : null;
    };
    const salesEnd = lastOf(salesSegments);
    const targetEnd = lastOf(targetSegments);
    /* Only worth marking where the stack actually rises above the paid line —
       otherwise the dot lands exactly on the paid one and says nothing. */
    const possibleEnd = (hasAnyAwaiting || hasAnyDraft) ? lastOf(possibleSegments) : null;
    const possibleEndRises = possibleEnd && possibleEnd.possibleTop > possibleEnd.sales;

    // Two end labels at the same x would sit on top of each other — which only happens on
    // a finished year, where both lines run to June. Push the lower one under its point.
    const endLabelsCollide = salesEnd && targetEnd
        && Math.abs(xOf(salesEnd.index) - xOf(targetEnd.index)) < 40
        && Math.abs(yOf(salesEnd.sales) - yOf(targetEnd.target)) < 20;
    const salesAbove = !endLabelsCollide || yOf(salesEnd.sales) <= yOf(targetEnd.target);

    const active = activeIndex === null ? null : points[activeIndex];

    const handleKeyDown = (event) => {
        if (!points.length) return;
        const { key } = event;
        if (key === 'ArrowRight' || key === 'ArrowLeft') {
            event.preventDefault();
            const step = key === 'ArrowRight' ? 1 : -1;
            const from = activeIndex === null ? (step > 0 ? -1 : points.length) : activeIndex;
            setActiveIndex(Math.min(points.length - 1, Math.max(0, from + step)));
        } else if (key === 'Home') {
            event.preventDefault();
            setActiveIndex(0);
        } else if (key === 'End') {
            event.preventDefault();
            setActiveIndex(points.length - 1);
        } else if (key === 'Escape') {
            setActiveIndex(null);
        }
    };

    const summary = `Line chart of ${cumulative ? 'cumulative ' : ''}sales against target for `
        + `${periodLabel ?? 'the selected year'}. Use the left and right arrow keys to read each month, `
        + 'or switch to the Numbers tab for the same figures as a table.';

    return (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* ---- Header & legend ---- */}
            <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">
                    {weekly
                        ? `Paid vs target by week · ${periodLabel ?? 'this month'}`
                        : (cumulative ? 'Cumulative paid vs target' : 'Paid vs target by month')}
                </h3>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1.5">
                        <LegendKey color={TARGET_COLOR} dashed /> Target
                    </span>
                    <span className="flex items-center gap-1.5">
                        <LegendKey color={SALES_COLOR} /> Paid
                    </span>
                    {/* Only shown when there is something in the band. A workshop paid on
                        the spot should not be given two legend keys explaining nothing. */}
                    {hasAnyAwaiting && (
                        <span className="flex items-center gap-1.5">
                            <LegendKey color={AWAITING_COLOR} /> Sent, not paid
                        </span>
                    )}
                    {hasAnyDraft && (
                        <span className="flex items-center gap-1.5">
                            <LegendKey color={DRAFT_COLOR} /> Draft
                        </span>
                    )}
                </div>
            </div>

            {/* ---- No targets yet ---- */}
            {!hasAnyTarget && (
                <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 bg-amber-50 border-b border-amber-100">
                    <p className="text-xs text-amber-900">
                        No targets set for {periodLabel ?? 'this year'}, so only the sales line is drawn.
                    </p>
                    {onSetTarget && (
                        <button
                            onClick={onSetTarget}
                            className="flex items-center gap-1.5 text-xs font-medium text-amber-900 hover:text-amber-950 underline underline-offset-2"
                        >
                            <Target size={13} /> Set a target
                        </button>
                    )}
                </div>
            )}

            {/* ---- Plot ---- */}
            <div
                ref={wrapRef}
                className="relative px-1 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset"
                tabIndex={0}
                role="img"
                aria-label={summary}
                onKeyDown={handleKeyDown}
                onBlur={() => setActiveIndex(null)}
                onMouseLeave={() => setActiveIndex(null)}
            >
                <svg width={width} height={HEIGHT} className="block" aria-hidden="true">
                    {/* Gridlines: hairline, solid, one step off the surface. */}
                    {ticks.map((tick) => (
                        <g key={tick}>
                            <line
                                x1={PAD.left} x2={PAD.left + plotWidth}
                                y1={yOf(tick)} y2={yOf(tick)}
                                stroke={tick === 0 ? AXIS : GRID} strokeWidth="1"
                            />
                            <text
                                x={PAD.left - 10} y={yOf(tick) + 4}
                                textAnchor="end" fontSize="11" fill="#6b7280"
                            >
                                {formatAxisTick(tick)}
                            </text>
                        </g>
                    ))}

                    {/* Month ticks. */}
                    {points.map((p) => (
                        <text
                            key={p.key}
                            x={xOf(p.index)} y={HEIGHT - PAD.bottom + 18}
                            textAnchor="middle" fontSize="11"
                            fill={p.isCurrent ? '#374151' : '#9ca3af'}
                            fontWeight={p.isCurrent ? 600 : 400}
                        >
                            {p.monthShort}
                            {p.showYear && (
                                <tspan x={xOf(p.index)} dy="12" fontSize="10" fill="#9ca3af" fontWeight={400}>
                                    {p.year}
                                </tspan>
                            )}
                        </text>
                    ))}

                    {/* Crosshair sits under the marks so it never covers a dot. */}
                    {active && (
                        <line
                            x1={xOf(active.index)} x2={xOf(active.index)}
                            y1={PAD.top} y2={PAD.top + plotHeight}
                            stroke="#9ca3af" strokeWidth="1"
                        />
                    )}

                    {/* ── The two bands above the paid line ──
                        Drawn first, so the target and paid lines sit on top of them and
                        stay readable. Draft on the outside, awaiting next to paid: the
                        further from money-in-the-bank, the fainter it gets. */}
                    {hasAnyDraft && possibleSegments.map((run) => (
                        <path
                            key={`band-d-${run[0].key}`}
                            d={bandOf(run, 'possibleTop', 'awaitingTop')}
                            fill={DRAFT_COLOR} fillOpacity="0.35" stroke="none"
                        />
                    ))}
                    {hasAnyAwaiting && awaitingSegments.map((run) => (
                        <path
                            key={`band-a-${run[0].key}`}
                            d={bandOf(run, 'awaitingTop', 'paidTop')}
                            fill={AWAITING_COLOR} fillOpacity="0.35" stroke="none"
                        />
                    ))}

                    {/* Target — dashed, so it reads as the goal and stays distinguishable
                        from sales in greyscale and for a red-green colourblind reader. */}
                    {targetSegments.map((run) => (
                        <path
                            key={`t-${run[0].key}`}
                            d={pathOf(run, 'target')}
                            fill="none" stroke={TARGET_COLOR} strokeWidth="2"
                            strokeDasharray="6 5" strokeLinecap="round" strokeLinejoin="round"
                        />
                    ))}
                    {/* A month whose neighbours have no target is a lone point with no line
                        to carry it, so it gets a dot of its own. */}
                    {targetSegments.filter((run) => run.length === 1).map((run) => (
                        <circle
                            key={`td-${run[0].key}`}
                            cx={xOf(run[0].index)} cy={yOf(run[0].target)} r="4"
                            fill={TARGET_COLOR} stroke={SURFACE} strokeWidth="2"
                        />
                    ))}

                    {/* Sales — solid. */}
                    {salesSegments.map((run) => (
                        <path
                            key={`s-${run[0].key}`}
                            d={pathOf(run, 'sales')}
                            fill="none" stroke={SALES_COLOR} strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                        />
                    ))}
                    {points.filter((p) => p.sales !== null).map((p) => (
                        <circle
                            key={`sd-${p.key}`}
                            cx={xOf(p.index)} cy={yOf(p.sales)}
                            r={p.isCurrent ? 5 : 4}
                            fill={SALES_COLOR} stroke={SURFACE} strokeWidth="2"
                        />
                    ))}

                    {/* The top of the stack: where the month lands if everything still
                        owed and everything unbilled comes in. Marked in the band's own
                        colour, and only where it actually sits above the paid line. */}
                    {(hasAnyAwaiting || hasAnyDraft) && points
                        .filter((p) => p.possibleTop !== null && p.possibleTop > p.sales)
                        .map((p) => (
                            <circle
                                key={`pd-${p.key}`}
                                cx={xOf(p.index)} cy={yOf(p.possibleTop)}
                                r={p.isCurrent ? 4.5 : 3.5}
                                fill={hasAnyDraft ? DRAFT_COLOR : AWAITING_COLOR}
                                stroke={SURFACE} strokeWidth="2"
                            />
                        ))}

                    {/* Direct end labels — text in ink, never in the series colour. */}
                    {salesEnd && (
                        <text
                            x={xOf(salesEnd.index) + 10}
                            y={yOf(salesEnd.sales) + (salesAbove ? -8 : 16)}
                            fontSize="11" fontWeight="600" fill="#111827"
                            stroke={SURFACE} strokeWidth="3.5" strokeLinejoin="round" paintOrder="stroke"
                        >
                            {formatEndLabel(salesEnd.sales)}
                        </text>
                    )}
                    {possibleEndRises && (
                        <text
                            x={xOf(possibleEnd.index) + 10}
                            y={yOf(possibleEnd.possibleTop) - 8}
                            fontSize="11" fontWeight="600" fill="#a16207"
                            fillOpacity="0.85"
                            stroke={SURFACE} strokeWidth="3.5" strokeLinejoin="round" paintOrder="stroke"
                        >
                            {formatEndLabel(possibleEnd.possibleTop)}
                        </text>
                    )}
                    {targetEnd && (
                        <text
                            x={xOf(targetEnd.index) + 10}
                            y={yOf(targetEnd.target) + (salesAbove ? 16 : -8)}
                            fontSize="11" fontWeight="600" fill="#4b5563"
                            stroke={SURFACE} strokeWidth="3.5" strokeLinejoin="round" paintOrder="stroke"
                        >
                            {formatEndLabel(targetEnd.target)}
                        </text>
                    )}

                    {/* Hit targets: a full-height band per month, so the pointer only has to
                        be nearest the right month rather than land on a 4px dot. */}
                    {points.map((p) => (
                        <rect
                            key={`hit-${p.key}`}
                            x={xOf(p.index) - (points.length > 1 ? plotWidth / (points.length - 1) / 2 : plotWidth / 2)}
                            y={PAD.top}
                            width={points.length > 1 ? plotWidth / (points.length - 1) : plotWidth}
                            height={plotHeight}
                            fill="transparent"
                            onMouseEnter={() => setActiveIndex(p.index)}
                        />
                    ))}
                </svg>

                {/* ---- Readout ---- */}
                {active && (
                    <div
                        className="pointer-events-none absolute z-10 w-52 rounded-lg border border-gray-200 bg-white p-2.5 shadow-lg"
                        style={{
                            left: Math.min(Math.max(xOf(active.index) + 14, 4), Math.max(width - 216, 4)),
                            top: 12,
                        }}
                    >
                        <div className="text-xs font-medium text-gray-500">
                            {active.label}
                            {active.isCurrent && <span className="ml-1 text-indigo-600">in progress</span>}
                            {active.isFuture && <span className="ml-1 text-gray-400">not yet</span>}
                        </div>
                        <div className="mt-1.5 space-y-1">
                            <div className="flex items-baseline gap-1.5">
                                <LegendKey color={SALES_COLOR} />
                                <span className="text-sm font-semibold tabular-nums text-gray-900">
                                    {active.sales === null ? '—' : formatFull(active.sales)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {cumulative ? 'paid to date' : 'paid'}
                                </span>
                            </div>
                            {hasAnyAwaiting && (
                                <div className="flex items-baseline gap-1.5">
                                    <LegendKey color={AWAITING_COLOR} />
                                    <span className="text-sm font-semibold tabular-nums text-gray-900">
                                        {active.awaiting === null ? '—' : formatFull(active.awaiting)}
                                    </span>
                                    <span className="text-xs text-gray-500">sent, not paid</span>
                                </div>
                            )}
                            {hasAnyDraft && (
                                <div className="flex items-baseline gap-1.5">
                                    <LegendKey color={DRAFT_COLOR} />
                                    <span className="text-sm font-semibold tabular-nums text-gray-900">
                                        {active.draft === null ? '—' : formatFull(active.draft)}
                                    </span>
                                    <span className="text-xs text-gray-500">draft</span>
                                </div>
                            )}
                            <div className="flex items-baseline gap-1.5">
                                <LegendKey color={TARGET_COLOR} dashed />
                                <span className="text-sm font-semibold tabular-nums text-gray-900">
                                    {active.target === null ? '—' : formatFull(active.target)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {cumulative ? 'target to date' : 'target'}
                                </span>
                            </div>
                        </div>
                        {active.sales !== null && active.target !== null && (
                            <div className="mt-1.5 border-t border-gray-100 pt-1.5 text-xs">
                                <span className={active.sales - active.target >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                                    {formatSigned(active.sales - active.target)}
                                </span>
                                <span className="text-gray-500"> vs target</span>
                            </div>
                        )}
                        {/* The number the whole feature exists for: chase the two upper
                            bands and this is where the month lands. Only worth saying
                            when it would change the verdict against target. */}
                        {active.possibleTop !== null && active.target !== null
                            && active.possibleTop > active.sales && (
                            <div className="mt-1 text-xs text-gray-500">
                                {formatFull(active.possibleTop)} if all collected
                                {active.possibleTop >= active.target && active.sales < active.target
                                    && <span className="ml-1 font-medium text-emerald-700">— hits target</span>}
                            </div>
                        )}
                        {cumulative && active.monthTarget === null && active.target !== null && (
                            <div className="mt-1 text-[11px] text-gray-400">No target set for this month.</div>
                        )}
                    </div>
                )}
            </div>

            {/* ---- Footnotes ---- */}
            <div className="px-4 pb-3 -mt-1 space-y-1">
                <p className="text-xs text-gray-500">
                    {/* ⚠️ This said "invoiced totals" until the line moved to money
                        received. The sentence is what tells an owner why the number is
                        smaller than their invoicing, so it has to say which it is. */}
                    The line is GST-inclusive money RECEIVED, which is how the target is judged. The bands
                    above it are billed-and-unpaid and unbilled draft work, so the top is where
                    {weekly ? ' the week' : ' the month'} lands if it all comes in. It stops at
                    {weekly ? ' the current week' : ' the current month'} rather than plotting
                    {weekly ? ' weeks' : ' months'} that have not happened
                    {weekly && ', and each week\u2019s target is this month\u2019s split across its days'}.
                </p>
                {cumulative && hasAnyTarget && targetIsPartial && (
                    <p className="text-xs text-gray-500">
                        The cumulative target only adds up the months that have a target, so it is lower
                        than a full year&rsquo;s goal.
                    </p>
                )}
            </div>
        </div>
    );
};

export default IncomeChart;
